import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout, Spin } from 'antd';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import { useAppSelector } from './store/hooks';
import { Role } from './constants/roles';
import { Permission } from './constants/permissions';
import { hasRole, hasAnyPermission, hasPermission } from './utils/permission';

const { Content } = Layout;

// 使用 React.lazy 懒加载页面组件
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const Workshops = lazy(() => import('./pages/Workshops'));
const Orders = lazy(() => import('./pages/Orders'));
const Login = lazy(() => import('./pages/Login'));
const Growth = lazy(() => import('./pages/Growth'));
const MiniappConfig = lazy(() => import('./pages/miniapp/Config'));
const Settings = lazy(() => import('./pages/Settings'));
const HomeCards = lazy(() => import('./pages/settings/HomeCards'));
const ProductScrollingLines = lazy(() => import('./pages/settings/ProductScrollingLines'));
const AboutSettings = lazy(() => import('./pages/settings/AboutSettings'));
const AdminUsers = lazy(() => import('./pages/users/AdminUsers'));
const WorkshopUsers = lazy(() => import('./pages/users/WorkshopUsers'));
const CustomerUsers = lazy(() => import('./pages/users/CustomerUsers'));
const ProductStock = lazy(() => import('./pages/stock/ProductStock'));
const VariantStock = lazy(() => import('./pages/stock/VariantStock'));
const WorkshopStock = lazy(() => import('./pages/stock/WorkshopStock'));
const Fabric = lazy(() => import('./pages/Fabric'));
const ProductCategories = lazy(() => import('./pages/ProductCategories'));
const ProgressStepTemplates = lazy(() => import('./pages/ProgressStepTemplates'));
const AppNotifications = lazy(() => import('./pages/app/Notifications'));

// 加载状态组件
const PageLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
    <Spin size="large">
      <div style={{ padding: '50px' }}>页面加载中...</div>
    </Spin>
  </div>
);

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const token = useAppSelector((state) => state.user.token) || localStorage.getItem('accessToken');
  const userInfo = useAppSelector((state) => state.user.userInfo);
  
  // 检查 token 和用户信息是否存在
  if (!token || !userInfo) {
    // 清除可能存在的无效 token
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userInfo');
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

function RoleRoute({ allowedRoles, children }: { allowedRoles: string[]; children: React.ReactNode }) {
  const userInfo = useAppSelector((state) => state.user.userInfo);
  
  if (!hasRole(userInfo, allowedRoles)) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

function PermissionRoute({ permissions, children }: { permissions: (Permission | string)[]; children: React.ReactNode }) {
  const userInfo = useAppSelector((state) => state.user.userInfo);

  if (!hasAnyPermission(userInfo, permissions)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function HomeRedirect() {
  const userInfo = useAppSelector((state) => state.user.userInfo);

  // 仅拥有库存权限的用户，访问 / 时重定向到库存管理页
  if (
    userInfo &&
    !hasPermission(userInfo, Permission.USER_MANAGE) &&
    hasPermission(userInfo, Permission.STOCK_READ)
  ) {
    return <Navigate to="/stock/product" replace />;
  }

  return <Home />;
}

function App() {
  const token = useAppSelector((state) => state.user.token) || localStorage.getItem('accessToken');
  
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        <Route path="/login" element={token ? <Navigate to="/" replace /> : <Login />} />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <Layout style={{ minHeight: '100vh' }}>
                <Sidebar />
                <Layout>
                  <Header />
                  <Content style={{ margin: '16px', padding: '24px', background: '#fff' }}>
                    <Suspense fallback={<PageLoading />}>
                      <Routes>
                        <Route path="/" element={<HomeRedirect />} />
                        <Route path="/products" element={<RoleRoute allowedRoles={[Role.ADMIN]}><Products /></RoleRoute>} />
                        <Route path="/products/fabric" element={<RoleRoute allowedRoles={[Role.ADMIN]}><Fabric /></RoleRoute>} />
                        <Route path="/products/categories" element={<RoleRoute allowedRoles={[Role.ADMIN]}><ProductCategories /></RoleRoute>} />
                        <Route path="/products/progress-templates" element={<RoleRoute allowedRoles={[Role.ADMIN]}><ProgressStepTemplates /></RoleRoute>} />
                        <Route path="/workshops" element={<RoleRoute allowedRoles={[Role.ADMIN]}><Workshops /></RoleRoute>} />
                        <Route path="/orders" element={<RoleRoute allowedRoles={[Role.ADMIN]}><Orders /></RoleRoute>} />
                        <Route path="/users/admin" element={<RoleRoute allowedRoles={[Role.ADMIN]}><AdminUsers /></RoleRoute>} />
                        <Route path="/users/workshop" element={<RoleRoute allowedRoles={[Role.ADMIN]}><WorkshopUsers /></RoleRoute>} />
                        <Route path="/users/customer" element={<RoleRoute allowedRoles={[Role.ADMIN]}><CustomerUsers /></RoleRoute>} />
                        <Route path="/stock/product" element={<PermissionRoute permissions={[Permission.STOCK_READ]}><ProductStock /></PermissionRoute>} />
                        <Route path="/stock/variant" element={<PermissionRoute permissions={[Permission.STOCK_READ]}><VariantStock /></PermissionRoute>} />
                        <Route path="/stock/workshop" element={<PermissionRoute permissions={[Permission.STOCK_READ]}><WorkshopStock /></PermissionRoute>} />
                        <Route path="/growth" element={<RoleRoute allowedRoles={[Role.ADMIN]}><Growth /></RoleRoute>} />
                        <Route path="/miniapp/config" element={<RoleRoute allowedRoles={[Role.ADMIN]}><MiniappConfig /></RoleRoute>} />
                        <Route path="/settings" element={<RoleRoute allowedRoles={[Role.ADMIN]}><Settings /></RoleRoute>} />
                        <Route path="/miniapp/home-cards" element={<RoleRoute allowedRoles={[Role.ADMIN]}><HomeCards /></RoleRoute>} />
                        <Route path="/miniapp/scrolling-lines" element={<RoleRoute allowedRoles={[Role.ADMIN]}><ProductScrollingLines /></RoleRoute>} />
                        <Route path="/miniapp/about" element={<RoleRoute allowedRoles={[Role.ADMIN]}><AboutSettings /></RoleRoute>} />
                        <Route path="/app/notifications" element={<RoleRoute allowedRoles={[Role.ADMIN]}><AppNotifications /></RoleRoute>} />
                      </Routes>
                    </Suspense>
                  </Content>
                </Layout>
              </Layout>
            </PrivateRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
